import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
  dismissible?: boolean;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  // Signal-based toast queue (reactive)
  private toastsSignal = signal<Toast[]>([]);

  // Public readonly signal for components to consume
  toasts = this.toastsSignal.asReadonly();

  // Auto-dismiss timers storage
  private timers = new Map<string, number>();

  // Default configuration
  private readonly DEFAULT_DURATION = 4000; // 4 seconds
  private readonly MAX_TOASTS = 5; // Maximum toasts visible at once

  /**
   * Generic show method - foundation for all toast types
   */
  show(toast: Omit<Toast, 'id' | 'timestamp'>): string {
    const id = this.generateId();
    const fullToast: Toast = {
      id,
      timestamp: Date.now(),
      duration: toast.duration ?? this.DEFAULT_DURATION,
      dismissible: toast.dismissible ?? true,
      ...toast
    };

    // Add to queue, maintaining max limit
    const currentToasts = this.toastsSignal();
    if (currentToasts.length >= this.MAX_TOASTS) {
      // Remove oldest toast
      const oldest = currentToasts[0];
      this.remove(oldest.id);
    }

    this.toastsSignal.update(toasts => [...toasts, fullToast]);

    // Setup auto-dismiss if duration is set
    if (fullToast.duration && fullToast.duration > 0) {
      this.scheduleAutoDismiss(id, fullToast.duration);
    }

    return id;
  }

  /**
   * Convenience methods for each toast type
   */
  success(message: string, title?: string, duration?: number): string {
    return this.show({
      type: 'success',
      message,
      title: title || 'Success',
      duration
    });
  }

  error(message: string, title?: string, duration?: number): string {
    return this.show({
      type: 'error',
      message,
      title: title || 'Error',
      duration: duration || 5000 // Errors stay longer
    });
  }

  info(message: string, title?: string, duration?: number): string {
    return this.show({
      type: 'info',
      message,
      title: title || 'Info',
      duration
    });
  }

  warning(message: string, title?: string, duration?: number): string {
    return this.show({
      type: 'warning',
      message,
      title: title || 'Warning',
      duration
    });
  }

  /**
   * Remove a specific toast
   */
  remove(id: string): void {
    this.clearTimer(id);
    this.toastsSignal.update(toasts => toasts.filter(t => t.id !== id));
  }

  /**
   * Clear all toasts
   */
  clearAll(): void {
    this.toastsSignal().forEach(toast => this.clearTimer(toast.id));
    this.toastsSignal.set([]);
  }

  /**
   * Schedule auto-dismiss for a toast
   */
  private scheduleAutoDismiss(id: string, duration: number): void {
    const timer = window.setTimeout(() => {
      this.remove(id);
    }, duration);

    this.timers.set(id, timer);
  }

  /**
   * Clear auto-dismiss timer
   */
  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  /**
   * Generate unique toast ID
   */
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
